import React, { useEffect, useMemo } from 'react';
import {
  Controller,
  DefaultValues,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import { useLiveSchema } from '@liveschema/react';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { translatePaymentMethodToCzech } from 'payment-method-translator';
import { Block } from '@/components/form/Block';
import { FormLabel } from '@/components/form/FormLabel';
import { PaymentMethod, SeminarType } from '@/graphql/codegen/graphql';

import {
  invoiceOptions,
  InvoiceTypeOptions,
} from '@/components/form/RadioButtonOption';
import {
  branchOffices,
  form,
  formStandardSchema,
  RegisterFormSchema,
} from '@/components/form/register-form-schema';
import { Dropzone } from '@/components/Dropzone';
import { useFileUpload } from '@/utils/useFileUpload';

interface RegisterFormProps {
  isSubmitting: boolean;
  onSubmit: (values: RegisterFormSchema) => void;
  seminarType: SeminarType;
  isForFree: boolean;
}

type AllKeys<T> = T extends unknown ? keyof T : never;
type FieldKey = AllKeys<RegisterFormSchema>;
type FieldErrorMap = Partial<Record<FieldKey, { message?: string } | undefined>>;

export const RegisterForm: React.FC<RegisterFormProps> = ({
  onSubmit,
  isSubmitting,
  seminarType,
  isForFree,
}) => {
  const {
    register,
    formState: { errors: formErrors },
    handleSubmit,
    control,
    setValue,
  } = useForm<RegisterFormSchema>({
    resolver: standardSchemaResolver(formStandardSchema),
    defaultValues: {
      seminarType,
      isForFree,
      paymentMethod: isForFree ? PaymentMethod.Free : PaymentMethod.Invoice,
      invoiceType: InvoiceTypeOptions.SCHOOL,
    } as DefaultValues<RegisterFormSchema>,
  });

  const watchedValues = useWatch({ control });
  const values = useMemo<Partial<RegisterFormSchema>>(
    () =>
      ({ ...watchedValues, seminarType, isForFree }) as Partial<
        RegisterFormSchema
      >,
    [watchedValues, seminarType, isForFree],
  );

  const { fields, isActiveField } = useLiveSchema(form, values);

  const paymentMethodOptions = fields.paymentMethod.enumOptions ?? [];

  const errors = formErrors as FieldErrorMap;

  const invoiceSchoolField = (noBorder: boolean) =>
    isActiveField('invoiceSchool') && (
      <Block noBorder={noBorder}>
        <FormLabel>Název a adresa školy</FormLabel>
        <TextField
          sx={{ mb: 2 }}
          variant="standard"
          fullWidth
          placeholder="např. ZŠ T. G. Masaryka, Třebíč, Komenského nám. 61/6, 674 01 Třebíč"
          required
          type="text"
          error={!!errors.invoiceSchool}
          helperText={errors.invoiceSchool?.message ?? ''}
          {...register('invoiceSchool')}
        />
      </Block>
    );

  const invoiceIcoField = (noBorder: boolean) =>
    isActiveField('invoiceIco') && (
      <Block noBorder={noBorder}>
        <FormLabel>IČO</FormLabel>
        <TextField
          sx={{ mb: 2 }}
          variant="standard"
          placeholder="např. 60418613"
          fullWidth
          required
          type="text"
          error={!!errors.invoiceIco}
          helperText={errors.invoiceIco?.message ?? ''}
          {...register('invoiceIco')}
        />
      </Block>
    );
  const fileUrls =
    ((watchedValues as { fileUrls?: string[] }).fileUrls ?? []) as string[];
  const { uploadFiles, isPending: isUploadingFiles } = useFileUpload();

  useEffect(() => {
    if (Object.keys(formErrors).length > 0) {
      console.log('errors', formErrors);
    }
  }, [formErrors]);

  const onSubmitHandler: SubmitHandler<RegisterFormSchema> = (formValues) => {
    onSubmit(formValues);
  };

  const consentNote =
    seminarType === SeminarType.Studium
      ? 'Vaši přihlášku pro studium je možné z vážných důvodů zrušit nejpozději do 21 kalendářních dnů před zahájením studia na emailu organizačního garanta. Děkujeme za pochopení.'
      : `Pokud se z vážných důvodů nemůžete plánované akce zúčastnit (ani za sebe, po domluvě s námi, vyslat náhradníka), zažádejte písemnou formou – emailem, nejpozději 5 pracovních dnů před zahájením akce, o zrušení Vaší přihlášky. ${
          seminarType === SeminarType.Idz
            ? ''
            : 'Pokud se akce nezúčastníte bez omluvy v daném termínu, rezervace místa Vám bude vyfakturována.'
        }`;

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit(onSubmitHandler)}
    >
      <Block>
        <FormLabel description="Budete mít uvedeno na osvědčení.">
          Jméno a příjmení účastníka vč. titulu
        </FormLabel>
        <TextField
          sx={{ mb: 2 }}
          variant="standard"
          fullWidth
          required
          error={!!errors.name}
          helperText={errors.name?.message ?? ''}
          {...register('name')}
        />
      </Block>

      {isActiveField('address') && (
        <Block>
          <FormLabel description="Uveďte trvalou adresu účastníka včetně PSČ.">
            Adresa
          </FormLabel>
          <TextField
            sx={{ mb: 2 }}
            variant="standard"
            fullWidth
            required
            type="text"
            error={!!errors.address}
            helperText={errors.address?.message ?? ''}
            {...register('address')}
          />
        </Block>
      )}

      <Block>
        <FormLabel description="Na tento e-mail obdrží účastník organizační pokyny ke konání kurzu. Uveďte, prosím, aktuální email, na který chodíte, buď soukromý nebo pracovní.">
          E-mail účastníka
        </FormLabel>
        <TextField
          sx={{ mb: 2 }}
          variant="standard"
          fullWidth
          required
          type="email"
          error={!!errors.email}
          helperText={errors.email?.message ?? ''}
          {...register('email')}
        />
      </Block>

      <Block>
        <FormLabel description="Datum bude použito pouze pro potřeby vystavení certifikátu.">
          Datum narození
        </FormLabel>
        <TextField
          sx={{ mb: 2 }}
          variant="standard"
          fullWidth
          required
          type="date"
          error={!!errors.dateOfBirth}
          helperText={errors.dateOfBirth?.message ?? ''}
          {...register('dateOfBirth')}
        />
      </Block>

      {isActiveField('placeOfBirth') && (
        <Block>
          <FormLabel description="Uveďte místo narození účastníka.">
            Místo narození
          </FormLabel>
          <TextField
            sx={{ mb: 2 }}
            variant="standard"
            fullWidth
            required
            type="text"
            error={!!errors.placeOfBirth}
            helperText={errors.placeOfBirth?.message ?? ''}
            {...register('placeOfBirth')}
          />
        </Block>
      )}

      {isActiveField('education') && (
        <Block>
          <FormLabel description="Uveďte nejvyšší dosažené vzdělání">
            Dosažené vzdělání
          </FormLabel>
          <TextField
            sx={{ mb: 4 }}
            variant="standard"
            fullWidth
            required
            type="text"
            error={!!errors.education}
            helperText={errors.education?.message ?? ''}
            {...register('education')}
          />

          <FormLabel description="Nahrajte výuční list, maturitní vysvědčení, diplom nebo jiný dokument">
            {null}
          </FormLabel>
          <Box sx={{ mt: 2 }}>
            <Dropzone
              fileUrls={fileUrls}
              isUploadingFiles={isUploadingFiles}
              onFilesDropped={async (files) => {
                const publicUrls = await uploadFiles(files);
                setValue('fileUrls', [...(fileUrls ?? []), ...publicUrls]);
                return publicUrls;
              }}
              onFileRemove={(fileIndex) => {
                const updated = fileUrls.filter((_, idx) => idx !== fileIndex);
                setValue('fileUrls', updated);
              }}
            />
          </Box>
        </Block>
      )}

      <Block>
        <FormLabel description="Uveďte číslo, na kterém bude přihlášený účastník k zastižení.">
          Mobilní telefon
        </FormLabel>
        <TextField
          sx={{ mb: 2 }}
          variant="standard"
          fullWidth
          required
          type="text"
          error={!!errors.phone}
          helperText={errors.phone?.message ?? ''}
          {...register('phone')}
        />
      </Block>

      {isActiveField('paymentMethod') && (
        <Block>
          <FormLabel>Platba</FormLabel>

          <FormControl>
            <Controller
              name="paymentMethod"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field}>
                  {paymentMethodOptions.map((value) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio />}
                      label={translatePaymentMethodToCzech(
                        value as PaymentMethod,
                      )}
                    />
                  ))}
                </RadioGroup>
              )}
            />
          </FormControl>
          {errors.paymentMethod?.message && (
            <FormHelperText error>
              {errors.paymentMethod.message}
            </FormHelperText>
          )}
        </Block>
      )}

      {isActiveField('invoiceType') ? (
        <Block>
          <FormLabel>Fakturační údaje</FormLabel>

          <Controller
            name="invoiceType"
            control={control}
            render={({ field }) => (
              <FormControl>
                <RadioGroup
                  {...field}
                  value={field.value ?? InvoiceTypeOptions.SCHOOL}
                  onChange={(_, value) => field.onChange(value)}
                >
                  {invoiceOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          />
          {errors.invoiceType?.message && (
            <FormHelperText error>{errors.invoiceType.message}</FormHelperText>
          )}

          {invoiceSchoolField(true)}
          {invoiceIcoField(true)}

          {isActiveField('invoiceEmail') && (
            <Block noBorder>
              <FormLabel description="Fakturu Vám zašleme na tento email">
                Fakturační email
              </FormLabel>
              <TextField
                sx={{ mb: 2 }}
                variant="standard"
                placeholder="např. jan.novak@gmail.com"
                fullWidth
                required
                type="text"
                error={!!errors.invoiceEmail}
                helperText={errors.invoiceEmail?.message ?? ''}
                {...register('invoiceEmail')}
              />
            </Block>
          )}
        </Block>
      ) : (
        <>
          {invoiceSchoolField(false)}
          {invoiceIcoField(false)}
        </>
      )}

      {isActiveField('wantsAllowance') && (
        <Block>
          <FormLabel>Příspěvek na rekvalifikaci</FormLabel>
          <FormControlLabel
            control={<Checkbox />}
            {...register('wantsAllowance')}
            label={
              <Typography>
                Chci vystavit potvrzení na příspěvek na rekvalifikaci na ÚP
              </Typography>
            }
          />
          {errors.wantsAllowance?.message && (
            <FormHelperText error>
              {errors.wantsAllowance.message}
            </FormHelperText>
          )}

          {isActiveField('branchOffice') && (
            <FormControl fullWidth>
              <FormLabel>
                Vyberte, na které pobočce (je třeba mít vyřízeno s dostatečným
                předstihem před zahájením studia)
              </FormLabel>
              <Controller
                name="branchOffice"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={branchOffices}
                    value={field.value ?? ''}
                    onChange={(_, value) => field.onChange(value ?? '')}
                    onInputChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{ mb: 2 }}
                        variant="standard"
                        fullWidth
                        required
                        type="text"
                        error={!!errors.branchOffice}
                      />
                    )}
                  />
                )}
              />
              {errors.branchOffice?.message && (
                <FormHelperText error>
                  {errors.branchOffice.message}
                </FormHelperText>
              )}
            </FormControl>
          )}
        </Block>
      )}

      <Block>
        <FormLabel>Poznámka</FormLabel>
        <TextField
          sx={{ mb: 2 }}
          variant="standard"
          fullWidth
          placeholder={
            seminarType === SeminarType.Idz ||
            seminarType === SeminarType.Digcompedu
              ? ''
              : 'např.: Prosím o uvedení čísla a názvu projektu ... na faktuře, budeme platit z OP JAK.'
          }
          error={!!errors.note}
          helperText={errors.note?.message ?? ''}
          {...register('note')}
        />
      </Block>

      <Block>
        <FormLabel description={consentNote}>Podmínky</FormLabel>
        <FormControlLabel
          sx={{ mt: 2 }}
          control={<Checkbox required />}
          {...register('consent')}
          label={<Typography>Souhlasím s podmínkami registrace</Typography>}
        />
        {errors.consent?.message && (
          <FormHelperText error>{errors.consent.message}</FormHelperText>
        )}
      </Block>

      <Block>
        <FormLabel>
          Před odesláním zkontrolujte, prosím, <strong>všechny údaje</strong>,
          tj. vaše osobní údaje vč. titulu, které budou uvedeny na osvědčení,
          vaši emailovou adresu, adresu školy. Po odeslání přihlášky vám přijde
          automatická odpověď na vámi zadaný email.
        </FormLabel>
      </Block>

      <Box textAlign="center">
        <Button
          disabled={isSubmitting}
          variant="contained"
          fullWidth
          type="submit"
          sx={{ py: '0.8rem', mt: '1rem', maxWidth: '10rem' }}
        >
          Registrovat
        </Button>
      </Box>
    </Box>
  );
};
